// src/helpers/notifications/templates/crud.ts

import { TemplateMeta } from '../notificationConfig';
import { formatReadableDate } from 'helpers/date';

// turn "due_date" → "Due date"
function humanizeField(field: string) {
    const s = field.replace(/_/g, ' ');
    return s.charAt(0).toUpperCase() + s.slice(1);
}

// detect ISO timestamp strings (roughly)
function isIsoDateString(val: any): val is string {
    return (
        typeof val === 'string' &&
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val) &&
        !isNaN(Date.parse(val))
    );
}

interface ModelConfig {
    key: string;
    label: string;
    icons: [string, string, string, string];
    supportsStageChange?: boolean;
}

const MODEL_CRUD: ModelConfig[] = [
    { key: 'student', label: 'Student', icons: ['ri-user-add-line text-success', 'ri-user-settings-line text-warning', 'ri-user-unfollow-line text-danger', 'ri-git-merge-line text-info'], supportsStageChange: true },
    { key: 'application', label: 'Application', icons: ['ri-file-add-line text-success', 'ri-file-settings-line text-warning', ' ri-file-reduce-line text-danger', 'ri-git-merge-line text-info'], supportsStageChange: true },
    // { key: 'institute', label: 'Institute', icons: ['ri-building-line', 'ri-building-4-line', 'ri-building-3-line', 'ri-arrow-right-line text-info'], supportsStageChange: false },
    // { key: 'course', label: 'Course', icons: ['ri-book-add-line', 'ri-book-settings-line', 'ri-book-mark-line', 'ri-arrow-right-line text-info'], supportsStageChange: false },
    // { key: 'source', label: 'Source', icons: ['ri-database-add-line', 'ri-database-settings-line', 'ri-database-remove-line', 'ri-arrow-right-line text-info'], supportsStageChange: false },
    // { key: 'channel', label: 'Channel', icons: ['ri-voiceprint-line', 'ri-voiceprint-line', 'ri-voiceprint-off-line', 'ri-arrow-right-line text-info'], supportsStageChange: false },
    // { key: 'subagent', label: 'Subagent', icons: ['ri-user-add-line', 'ri-user-settings-line', 'ri-user-unfollow-line', 'ri-arrow-right-line text-info'], supportsStageChange: false },
];
/**
 * Optional per‐model subtitle overrides (and you can add other slots the same way).
 */
const SUBTITLE_OVERRIDES: Record<
    string,
    {
        created?: (d: any) => string;
        updated?: (d: any) => string;
        deleted?: (d: any) => string;
    }
> = {
    student: {
        created: d => `Source: ${d.source}`,
    },
    application: {
        created: d => `Program: ${d.program}`,
        // you could also do updated/deleted here…
    }
};


export function makeCrudTemplates(): TemplateMeta[] {
    return MODEL_CRUD.flatMap(({ key, label, icons, supportsStageChange }) => {
        const idField = `${key}_id`;
        const nameField = `${key}_name`;
        const overrides = SUBTITLE_OVERRIDES[key] || {};

        const base: TemplateMeta[] = [
            {
                key: `${key}.created`,
                label: `${label} Created`,
                type: 'updates',
                icon: icons[0],
                title: d => `${label} created: ${d[nameField]}`,
                subtitle: overrides.created
                ? overrides.created
                : d => `ID: ${d[idField]}`,
            },
            {
                key: `${key}.updated`,
                label: `${label} Updated`,
                type: 'updates',
                icon: icons[1],
                title: d => `${label} updated: ${d[nameField]}`,
                subtitle: d => {
                    if (!Array.isArray(d.changes) || d.changes.length === 0) {
                        return `${humanizeField(key)} details updated`;
                    }
                    return d.changes
                        .map((c: any) => {
                            const label = humanizeField(c.field);
                            if (isIsoDateString(c.new_value)) {
                                return `${label} → ${formatReadableDate(c.new_value)}`;
                            }
                            return `${label} → ${c.new_value}`;
                        })
                        .join(' · ');
                },
            },
            {
                key: `${key}.deleted`,
                label: `${label} Deleted`,
                type: 'updates',
                icon: icons[2],
                title: d => `${label} removed: ${d[nameField]}`,
            },
        ];

        if (supportsStageChange) {
            base.push({
              key:     `${key}.stage_changed`,
              label:   `${label} Stage Changed`,
              type:    'updates',
              icon:    icons[3],
              // pull the human-readable name out of `d[nameField]` (e.g. `student_name`)
              title:   d => `${label} ${d[nameField]} moved to ${d.to_stage}`,
              subtitle:d => `From ${d.from_stage} by ${d.changed_by}`,
            });
        }

        return base;
    });
}
